<template>
  <portal to="modals"> 
    <!-- overlay -->
    <div
      class="oc-overlay"
      v-if="isShown"
      @click="onOverlayClick"
      aria-hidden="true"
    ></div>

    <!-- offcanvas panel -->
    <transition :name="transition">
      <aside
        class="oc-panel"
        :class="[
          `oc--${position}`,
          { 'is-active': isShown },
          panelClass
        ]"
        :style="panelStyle"
        role="dialog"
        aria-modal="true"
      >
        <header class="oc-header">
          <slot name="header">
            <button class="oc-close-btn" @click="close">✕</button>
          </slot>
        </header>
        <div class="oc-body">
          <slot />
        </div>
        <footer v-if="$slots.footer" class="oc-footer">
          <slot name="footer" />
        </footer>
      </aside>
    </transition>
  </portal>
</template>

<script>
export default {
  name: "OffCanvas",
  props: {
    value: Boolean,
    position: {
      type: String,
      default: "right",
    },
    size: {
      type: String,
      default: "320px",
    },
    closeOnOverlay: {
      type: Boolean,
      default: true,
    },
    closeOnEsc: {
      type: Boolean,
      default: true,
    },
    panelClass: {
      type: [String, Array, Object],
      default: "",
    },
  },
  computed: {
    transition(){
      return `oc-slide-${this.position}`;
    },
    isShown:{
      get(){
        return this.value;
      },
      set(v){
        this.$emit("input", v);
      }
    },
    panelStyle() {
      return this.position === "left" || this.position === "right"
        ? { width: this.size }
        : { height: this.size };
    },
  },
  watch: {
    isShown(val) {
      // PURE side effects only (NO EMIT)
      if (val) {
        document.body.classList.add("oc-scroll-lock");
        if (this.closeOnEsc) {
          window.addEventListener("keydown", this.onKeyDown);
        }
      } else {
        document.body.classList.remove("oc-scroll-lock");
        window.removeEventListener("keydown", this.onKeyDown);
      }
    },
  },
  methods: {
    open() {
      this.$emit("input", true);
      this.$emit("open");
    },
    close() {
      this.$emit("input", false);
      this.$emit("close");
    },
    onOverlayClick() {
      if (this.closeOnOverlay) this.close();
    },
    onKeyDown(e) {
      if (e.key === "Escape") this.close();
    },
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeyDown);
    document.body.classList.remove("oc-scroll-lock");
  },
};
</script>

<style scoped>
/* overlay */
.oc-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 1050 !important;
  background: rgba(0, 0, 0, 0.45);
}

/* basic panel */
.oc-panel {
  position: fixed;
  background: #fff;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: auto;
  max-height: 100vh; 
  z-index: 1050 !important;
}

/* positions */
.oc--left {
  top: 0;
  bottom: 0;
  left: 0;
  /* transform: translateX(-100%); */
}
.oc--right {
  top: 0;
  bottom: 0;
  right: 0;
  /* transform: translateX(100%); */
}
.oc--top {
  left: 0;
  right: 0;
  top: 0;
  /* transform: translateY(-100%); */
}
.oc--bottom {
  left: 0;
  right: 0;
  bottom: 0;
  /* transform: translateY(100%); */
}

/* header, body, footer */
.oc-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.oc-body {
  padding: 16px;
  flex: 1 1 auto;
}
.oc-footer {
  padding: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  text-align: right;
}
.oc-close-btn {
  background: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;
}

/* prevent body scroll when open */
.oc-scroll-lock {
  overflow: hidden !important;
}

/* transitions: overlay fade */
.oc-fade-enter-active,
.oc-fade-leave-active {
  transition: opacity 0.22s ease;
}
.oc-fade-enter,
.oc-fade-leave-to {
  opacity: 0;
}

/* slide transitions per position */
.oc-slide-left-enter-active,
.oc-slide-left-leave-active,
.oc-slide-right-enter-active,
.oc-slide-right-leave-active,
.oc-slide-top-enter-active,
.oc-slide-top-leave-active,
.oc-slide-bottom-enter-active,
.oc-slide-bottom-leave-active {
  transition: transform 0.28s cubic-bezier(0.2, 0.9, 0.3, 1);
}

/* when entering, transform to zero */
.oc-slide-left-enter,
.oc-slide-left-leave-to {
  transform: translateX(-100%);
}
.oc-slide-left-enter-to,
.oc-slide-left-leave {
  transform: translateX(0);
}

.oc-slide-right-enter,
.oc-slide-right-leave-to {
  transform: translateX(100%);
}
.oc-slide-right-enter-to,
.oc-slide-right-leave {
  transform: translateX(0);
}

.oc-slide-top-enter,
.oc-slide-top-leave-to {
  transform: translateY(-100%);
}
.oc-slide-top-enter-to,
.oc-slide-top-leave {
  transform: translateY(0);
}

.oc-slide-bottom-enter,
.oc-slide-bottom-leave-to {
  transform: translateY(100%);
}
.oc-slide-bottom-enter-to,
.oc-slide-bottom-leave {
  transform: translateY(0);
}

/* small responsive tweak */
@media (max-width: 640px) {
  .oc-panel.oc--right,
  .oc-panel.oc--left {
    width: 100% !important; /* override size to full width on small screens */
  }
}
.oc-panel {
  transition: transform 0.28s cubic-bezier(0.2, 0.9, 0.3, 1);
}

.oc--right { transform: translateX(100%); }
.oc--right.is-active { transform: translateX(0); }

.oc--left { transform: translateX(-100%); }
.oc--left.is-active { transform: translateX(0); }

.oc--top { transform: translateY(-100%); }
.oc--top.is-active { transform: translateY(0); }

.oc--bottom { transform: translateY(100%); }
.oc--bottom.is-active { transform: translateY(0); }
 
</style>



// WEBPACK FOOTER //
// src/components/TextReply/offCanvas.vue